import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AgGridListComponent } from '../../components/ag-grid-list/ag-grid-list.component';
import { HeaderComponent } from '../../components/header/header.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { DateRangeComponent } from '../../components/date-range/date-range.component';
import { ButtonComponent } from '../../components/button/button.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { ActivityLogService } from '../../services/activity-log.service';
import { FormService } from '../../services/form.service';

@Component({
  selector: 'app-activity-log-list-page',
  imports: [CommonModule, FormsModule, HeaderComponent, AgGridListComponent, SearchInputComponent, SelectInputComponent, DateRangeComponent, ButtonComponent],
  templateUrl: './activity-log-list-page.component.html',
  styleUrl: './activity-log-list-page.component.css'
})
export class ActivityLogListPageComponent {

  page_name = 'Activity Log';
  params: any = {};
  items: any = [];
  date_range_value: any = {};

  role_options: any[] = [];
  user_options: any[] = [];

  entity_options: any[] = [
    { title: 'Country', value: 'Country' },
    { title: 'State', value: 'State' },
    { title: 'City', value: 'City' },
    { title: 'District', value: 'District' },
    { title: 'University', value: 'University' },
    { title: 'College', value: 'College' },
    { title: 'Program', value: 'Program' },
    { title: 'Course', value: 'Course' },
    { title: 'Subject', value: 'Subject' },
    { title: 'Stream', value: 'Stream' },
    { title: 'Level', value: 'Level' },
    { title: 'Academic Year', value: 'AcademicYear' },
    { title: 'Barcode', value: 'Barcode' },
    { title: 'Role', value: 'Role' },
    { title: 'User', value: 'User' },
    { title: 'Exam', value: 'Exam' },
    { title: 'Assessment', value: 'Assessment' },
    { title: 'Script', value: 'Script' },
    { title: 'Evaluation', value: 'Evaluation' },
    { title: 'Exception', value: 'Exception' },
    { title: 'Re-Evaluation', value: 'Re-Evaluation' },
    { title: 'Result', value: 'Result' },
    { title: 'Student Mapping', value: 'StudentMapping' },
    { title: 'Auth', value: 'Auth' },
  ];

  // Mirrors the backend's MODULE_MAP (get-activity-log-list.function.ts) — Sub-Module (entity)
  // to Module grouping, used to cascade the Sub-Module dropdown's options off the selected Module.
  entity_module_map: Record<string, string> = {
    Country: 'Masters', State: 'Masters', City: 'Masters', District: 'Masters', University: 'Masters', College: 'Masters', Program: 'Masters', Course: 'Masters', Subject: 'Masters', Stream: 'Masters', Level: 'Masters', AcademicYear: 'Masters',
    Barcode: 'Barcode',
    Role: 'User Management', User: 'User Management',
    Exam: 'Exam',
    Assessment: 'Assessment',
    Script: 'Script',
    Evaluation: 'Evaluation',
    Exception: 'Exception',
    'Re-Evaluation': 'Re-Evaluation',
    Result: 'Result',
    StudentMapping: 'Student Mapping',
    Auth: 'Auth',
  };

  module_options: any[] = [
    { title: 'Masters', value: 'Masters' },
    { title: 'Barcode', value: 'Barcode' },
    { title: 'User Management', value: 'User Management' },
    { title: 'Exam', value: 'Exam' },
    { title: 'Assessment', value: 'Assessment' },
    { title: 'Script', value: 'Script' },
    { title: 'Evaluation', value: 'Evaluation' },
    { title: 'Exception', value: 'Exception' },
    { title: 'Re-Evaluation', value: 'Re-Evaluation' },
    { title: 'Result', value: 'Result' },
    { title: 'Student Mapping', value: 'Student Mapping' },
    { title: 'Auth', value: 'Auth' },
  ];

  // Recomputed only when Module changes, never bound as a getter: a getter returning
  // `.filter(...)` hands ng-select a brand new array on every change detection run, which rebuilds
  // its internal item list mid-click and swallows the selection. With no Module picked the getter
  // returned the stable `entity_options` reference, which is why Sub-Module only stopped
  // responding once Module was set.
  filtered_entity_options: any[] = this.entity_options;

  update_entity_options() {
    this.filtered_entity_options = this.params.module
      ? this.entity_options.filter(o => this.entity_module_map[o.value] === this.params.module)
      : this.entity_options;
  }

  action_options: any[] = [
    { title: 'Add', value: 'Add' },
    { title: 'Update', value: 'Update' },
    { title: 'Delete', value: 'Delete' },
    { title: 'Assignment', value: 'Assignment' },
    { title: 'Mapping', value: 'Mapping' },
    { title: 'Evaluation', value: 'Evaluation' },
    { title: 'Exception', value: 'Exception' },
    { title: 'Re-Evaluation', value: 'Re-Evaluation' },
    { title: 'Publication', value: 'Publication' },
    { title: 'Login', value: 'Login' },
    { title: 'Logout', value: 'Logout' },
    { title: 'Upload', value: 'Upload' },
  ];

  bulk_columns: any = [
    { column_name: 'User Name', type: 'text', return_as: 'user_name' },
    { column_name: 'Role', type: 'text', return_as: 'role_name' },
    { column_name: 'Contact', type: 'text', return_as: 'contact' },
    { column_name: 'Email', type: 'text', return_as: 'email' },
    { column_name: 'Module', type: 'text', return_as: 'module' },
    { column_name: 'Sub-Module', type: 'text', return_as: 'sub_module' },
    { column_name: 'Date & Time', type: 'date', return_as: 'performed_at' },
    { column_name: 'Action', type: 'text', return_as: 'action' },
    { column_name: 'Description', type: 'text', return_as: 'description' },
  ];

  columns: any = [
    {
      headerName: 'User Name / Role', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const name = p.data?.user_name || '-';
        const role = p.data?.role_name || '';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;line-height:1.4"><span style="font-weight:600;font-size:13px">${name}</span>${role ? `<span style="font-size:12px;color:#6b7280">${role}</span>` : ''}</div>`;
      }
    },
    {
      headerName: 'Contact', flex: 1.5, minWidth: 140,
      cellRenderer: (p: any) => {
        const contact = p.data?.contact || '';
        const email = p.data?.email || '';
        if (!contact && !email) return '-';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;line-height:1.4"><span style="font-size:13px;font-weight:600">${contact || '-'}</span><span style="font-size:12px;color:#6b7280">${email}</span></div>`;
      }
    },
    { headerName: 'Module', field: 'module', flex: 1.5, minWidth: 130 },
    { headerName: 'Sub-Module', field: 'sub_module', flex: 1.5, minWidth: 130 },
    {
      headerName: 'Date & Time', flex: 1.5, minWidth: 150,
      valueGetter: (p: any) => {
        if (!p.data?.performed_at) return '-';
        const d = new Date(p.data.performed_at);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
      }
    },
    {
      headerName: 'Action Taken', flex: 2, minWidth: 180,
      cellStyle: { background: '#EBF2FF', overflow: 'auto' },
      cellRenderer: (p: any) => {
        const action = p.data?.action || '';
        const desc = p.data?.description || '';
        if (!action && !desc) return '-';
        const text = desc ? `${action} \u2013 ${desc}` : action;
        return `<div style="display:flex;align-items:center;height:100%;font-size:13px;font-weight:700;line-height:1.4;white-space:nowrap">${text}</div>`;
      }
    },
  ];

  constructor(public gs: GlobalService, public ar: ActivatedRoute, public als: ActivityLogService, public dds: DropdownService, public fs: FormService,) { }

  async ngOnInit() {
    await this.dds.get_dropdown_list(['role', 'user']);
    this.role_options = this.dds.dropdown_list?.role_list || [];
    this.user_options = this.dds.dropdown_list?.user_list || [];

    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params };
      this.update_entity_options();
      await this.get_list(this.params);
    });
  }

  async get_list(filter?: any) {
    try {
      // AgGridListComponent's pagination/sort events only carry page/page_size/sort_by/sort_order
      // — they know nothing about the filters below. Merging this.params (the current filter
      // state) first, then the incoming event on top, means changing page never drops a filter.
      const res = await this.als.get_list({ ...this.params, ...filter });
      this.items = res;
    } catch (error: any) {
      console.error('Error fetching activity logs:', error);
    }
  }

  apply_filters() {
    this.get_list({ page: 1 });
  }

  on_module_change() {
    // Clear a selected Sub-Module that no longer belongs to the newly-picked Module.
    if (this.params.entity && this.entity_module_map[this.params.entity] !== this.params.module) {
      this.params.entity = null;
    }
    this.update_entity_options();
    this.apply_filters();
  }

  on_search_change(value: any) {
    this.params.search = value || undefined;
    this.apply_filters();
  }

  on_date_range_change(value: { from_date?: string; to_date?: string }) {
    this.params.from_date = value?.from_date || undefined;
    this.params.to_date = value?.to_date || undefined;
    this.apply_filters();
  }

  download() {
    this.fs.bulk_download(this.als, 'get_list', this.bulk_columns, 'Activity Logs', { ...this.params });
  }
}
